/**
 * Match screen — canvas (WP6 stage) + DOM overlay.
 *
 * Layout:
 *   • Full-viewport #stage-container (WP6 mounts Pixi here).
 *   • Absolutely-positioned overlay elements above the canvas.
 *
 * Phase-driven sub-UI:
 *   • waiting:  share-link panel.
 *   • countdown: big number.
 *   • active:   combat HUD (ActionBar, CastBar, FrameCluster [#421 —
 *               OwnFrame/TargetFrame/FriendlyTargetFrame]).
 *   • ended:    winner banner + damage meter + Re-queue/Leave (#471).
 *
 * HUD size: the screen-anchored active-phase HUD is wrapped in per-anchor
 * `HudScaleRegion`s that apply the single `HUD_SCALE` knob (#755). The
 * world-anchored `NameplateAuras` and the `InputSurface` deliberately stay
 * OUTSIDE them — see `hudScale.tsx`.
 *
 * Snapshots: NOT read from React state (see store/types.ts). The canvas
 * layer draws from the WsSnapshotSource → WP6 ring buffer at 30 Hz. The
 * HUD reads `state.hudSnapshot` which the sampler updates at 10 Hz.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  mountRenderer,
  type Renderer,
  computeArenaCenter,
  worldToScreen,
  type ProjectionCenter,
} from "../render/index.js";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { useKeymap } from "../input/keybinding/index.js";
import { WaitingPanel } from "./WaitingPanel.js";
import { Countdown } from "./Countdown.js";
import { WinnerBanner } from "./WinnerBanner.js";
import { ActionBar } from "./ActionBar.js";
import { CastBar } from "./CastBar.js";
import { FrameCluster } from "./FrameCluster.js";
import { SelfAuraBar } from "./SelfAuraBar.js";
import { NameplateAuras, type EntityProjector } from "./NameplateAuras.js";
import { UnitFrameStack } from "./UnitFrameStack.js";
import { HudScaleRegion } from "./hudScale.js";
import { RejectionToast } from "./RejectionToast.js";
import { CastErrorNotice } from "./CastErrorNotice.js";
import { ChatInput } from "./ChatInput.js";
import { InputSurface } from "../input/InputSurface.js";
import { SettingsScreen } from "./SettingsScreen.js";
import { useHudSampler } from "../store/sampler.js";
import { useSound } from "../sound/SoundProvider.js";
import { arenaForSlug } from "../render/arenaCatalog.js";

const TICK_RATE = 30;

export interface MatchScreenProps {
  /** Optional demo-mode source; when unset, uses the live WS source. */
  readonly demoRenderer?: false;
}

export function MatchScreen(): ReactElement {
  const { state } = useStore();
  const client = useWsClient();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  // Latest own seat, read by the async mount + kept fresh for the
  // camera. React state (state.ownSeat) drives it; the ref avoids a
  // stale closure in the run-once mount effect. #731: `null` ("not yet
  // known" — before `match.joined`) is NEVER coerced to seat 0; the
  // renderer treats a `null` seat as "no followed entity yet" (holds
  // arena center) rather than piloting seat 0's camera.
  const ownSeatRef = useRef<number | null>(state.ownSeat);
  ownSeatRef.current = state.ownSeat;

  // #700: the ACTUAL arena geometry for this match (`state.mapSlug`, from
  // `matchCreated`/`matchPhase`), falling back to `LIVE_ARENA`/`bare` only
  // when unset (see `arenaForSlug`'s doc comment). Recomputed every render
  // (cheap linear scan over a handful of maps) so `getProjectionCenter`
  // below always reads the latest known value; the run-once mount effect
  // freezes it via `resolvedArenaRef` the same way `ownSeatRef` freezes seat.
  const resolvedArena = arenaForSlug(state.mapSlug);
  const resolvedArenaRef = useRef(resolvedArena);
  resolvedArenaRef.current = resolvedArena;

  // Live keymap for the target-hotkey nameplates (#136). The ref feeds the
  // run-once async mount; the effect below pushes rebinds live (same pattern as
  // ActionBar reading the live keymap so a rebind refreshes the badge).
  const { keymap } = useKeymap();
  const keymapRef = useRef(keymap);
  keymapRef.current = keymap;

  // Live camera center getter for input hit-testing. Reads the
  // renderer's per-frame follow camera; falls back to a static arena
  // center during the async-mount window before the renderer exists.
  const getProjectionCenter = (): ProjectionCenter =>
    rendererRef.current?.getCenter() ??
    computeArenaCenter(
      canvasRef.current?.clientWidth ?? 0,
      canvasRef.current?.clientHeight ?? 0,
      resolvedArena.width,
      resolvedArena.height,
    );

  // World→screen projector for the nameplate aura overlay (#137). Reads the
  // LIVE follow-camera center each render (10 Hz HUD cadence) so aura rows track
  // their unit's floating nameplate. Purely cosmetic — no sim/hash impact.
  const projectEntity: EntityProjector = (x, y) =>
    worldToScreen(x, y, getProjectionCenter());

  // Sample the WS snapshot source into the store at 10 Hz for HUD reads.
  useHudSampler(client.snapshotSource);

  // #795: the engine the renderer plays IMPACT audio through (an ability's
  // `.mp3` fires when its damage lands — there is no cast-time SFX any more).
  // Held in a ref for the run-once async mount below, same pattern as
  // seat/keymap/arena.
  const sound = useSound();
  const soundRef = useRef(sound);
  soundRef.current = sound;

  // Keep the camera's followed seat in sync with the store. #731: forwards
  // the REAL value (including `null` while unknown) — never a seat-0
  // fallback; `Renderer.setOwnSeat` accepts `null` and the follow camera
  // degrades to arena-center for it (see `mount.ts`'s `ownSeat` doc comment).
  useEffect(() => {
    rendererRef.current?.setOwnSeat(state.ownSeat);
  }, [state.ownSeat]);

  // #702 follow-up: push a CORRECTED arena into an already-mounted renderer
  // once the real `mapSlug` arrives. The mount effect below freezes its
  // arena at mount time via `resolvedArenaRef` (same pattern as `ownSeat`
  // freezing via `ownSeatRef`) — for a match creator this mount race is
  // essentially always already-correct (the wire message is a same-origin
  // round-trip that beats the renderer's own asset-load await), but a
  // JOINING teammate's fresh WS connect can have its renderer finish
  // mounting before its first `matchCreated`/`matchPhase` lands, freezing on
  // the `null`-slug fallback (`LIVE_ARENA`/`bare`) with nothing to
  // self-correct — this effect is that self-correction. A no-op call when
  // `state.mapSlug` was already correct at mount time (the common case);
  // cheap (one static redraw — see `stage.ts`'s `setArena` doc comment).
  // `resolvedArena` is re-derived from `state.mapSlug` every render (see
  // above); gating this effect on `[state.mapSlug]` re-runs it exactly when
  // the resolved arena can change, mirroring the `ownSeat` effect's
  // dependency shape.
  useEffect(() => {
    rendererRef.current?.setArena(resolvedArena);
  }, [state.mapSlug]);

  // Push keymap rebinds to the renderer so nameplate labels refresh live (#136).
  useEffect(() => {
    rendererRef.current?.setKeymap(keymap);
  }, [keymap]);

  // #855: push the chat display policy the same way — toggling chat off (or
  // muting a seat) silences the NEXT snapshot, not the next match. The store
  // holds both halves, so this effect is the only place they reach the
  // renderer.
  const chatPolicy = useMemo(
    () => ({ enabled: state.chatEnabled, mutedSeats: state.mutedSeats }),
    [state.chatEnabled, state.mutedSeats],
  );
  const chatPolicyRef = useRef(chatPolicy);
  chatPolicyRef.current = chatPolicy;
  useEffect(() => {
    rendererRef.current?.setChatPolicy(chatPolicy);
  }, [chatPolicy]);

  // Live own-champion for the renderer's 3D character path (#237) — a
  // fallback for the local seat only, used before the first `matchPhase`
  // roster arrives (the server always includes self from its very first
  // send, so this is belt-and-braces, not the steady-state source of truth).
  const ownChampionRef = useRef(state.ownChampionId);
  ownChampionRef.current = state.ownChampionId;

  // Live seat→championId roster (#244) for the renderer's 3D character path
  // (#237): the wire `matchPhase.roster` carries EVERY joined seat's
  // champion (not just the local one), so a 3D-enabled champion renders for
  // every observer, not only the player who picked it. The ref keeps the
  // run-once mount closure fresh.
  const rosterRef = useRef(state.roster);
  rosterRef.current = state.roster;

  // Mount WP6 renderer once (StrictMode double-mount safe via ref guard).
  useEffect(() => {
    if (canvasRef.current === null) return;
    if (rendererRef.current !== null) return;
    const container = canvasRef.current;
    let cancelled = false;
    void (async () => {
      const renderer = await mountRenderer({
        container,
        arena: resolvedArenaRef.current,
        tickRate: TICK_RATE,
        snapshotSource: client.snapshotSource,
        ownSeat: ownSeatRef.current,
        keymap: keymapRef.current,
        // Resolve ANY seat's champion off the live roster so a 3D-enabled
        // champion (the rogue, #237) renders as its 3D model for every
        // observer (#244) — not just the local player. Falls back to the
        // local selection for the own seat pre-roster.
        championIdForSeat: (seat) => {
          const entry = rosterRef.current.find((r) => r.seat === seat);
          if (entry !== undefined) return entry.championId;
          return seat === (ownSeatRef.current ?? -1) ? ownChampionRef.current : null;
        },
        // #795: impact audio. The renderer drains each snapshot's `damage`
        // events once and plays the responsible ability's committed sound; the
        // engine itself stays owned by `SoundProvider` (this only lends it).
        impactAudio: {
          play: (id) => {
            soundRef.current.play(id);
          },
          playFile: (url) => {
            soundRef.current.playFile(url);
          },
        },
        // #855: freeze the CURRENT policy at mount (same ref pattern as
        // seat/keymap/arena); the effect above pushes every later change.
        chatPolicy: chatPolicyRef.current,
      });
      if (cancelled) {
        renderer.dispose();
        return;
      }
      rendererRef.current = renderer;
      // The async mount can resolve after the seat/keymap effects already ran,
      // so push the latest now (the effects only fire on CHANGES).
      renderer.setOwnSeat(ownSeatRef.current);
      renderer.setKeymap(keymapRef.current);
      renderer.setChatPolicy(chatPolicyRef.current);
    })();
    return () => {
      cancelled = true;
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
    // Only run once — the camera's followed seat is updated separately
    // via setOwnSeat (see the state.ownSeat effect above); rerunning
    // this effect would remount the renderer, which is overkill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phase = state.phase.phase;
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={canvasRef}
        data-testid="match-canvas"
        style={{ position: "absolute", inset: 0 }}
      />
      <InputSurface getProjectionCenter={getProjectionCenter} />
      <RejectionToast />
      {/* #787: locally-predicted cast failures ("Not enough energy"). A
       *  SEPARATE channel from `RejectionToast` on purpose — see that file's
       *  "bug signal" header and `CastErrorNotice`'s own. Outside the scaled
       *  HUD regions: it is play-area-anchored, not HUD-anchored. */}
      <CastErrorNotice />
      {/* #833: the in-game chat compose box. Rendered OUTSIDE the phase
       *  conditionals below — chat is available whenever you're on the match
       *  screen, and the server independently drops anything sent outside
       *  countdown/active. Renders nothing while closed (and, since #855, it
       *  can never BE open while chat is disabled — the store refuses it).
       *  #855: `onLocalEcho` is the sender's own floater. It must not come
       *  from the server round-trip — see `ChatInputProps`. */}
      <ChatInput
        onLocalEcho={(text) => {
          rendererRef.current?.echoLocalChat(text);
        }}
      />

      <button
        type="button"
        data-testid="match-settings-open"
        onClick={() => {
          setSettingsOpen(true);
        }}
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 6,
          padding: "4px 10px",
          background: "#22303f",
          color: "#9fb0c3",
          border: "1px solid #3a4757",
          borderRadius: 6,
          fontSize: 12,
        }}
      >
        ⚙ Settings
      </button>

      {settingsOpen ? (
        <div
          data-testid="match-settings-overlay"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(10, 14, 20, 0.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SettingsScreen
            onClose={() => {
              setSettingsOpen(false);
            }}
          />
        </div>
      ) : null}

      {phase === "waiting" ? <WaitingPanel /> : null}
      {phase === "countdown" ? <Countdown /> : null}
      {phase === "active" ? (
        <>
          {/* #755: NOT inside a scaled region — `NameplateAuras` is
           *  WORLD-anchored (`left: project(e.x, e.y).sx`), so a parent
           *  `scale()` would multiply its projected coordinates and drift
           *  every chip row off its unit. See `hudScale.tsx`. */}
          <NameplateAuras project={projectEntity} />
          {/* #755: one scaled wrapper per anchor region, each with a
           *  `transform-origin` on the edge its contents are pinned to, so
           *  the HUD grows IN PLACE instead of floating toward the middle. */}
          <HudScaleRegion anchor="top">
            <FrameCluster />
            <SelfAuraBar />
          </HudScaleRegion>
          <HudScaleRegion anchor="bottom">
            <UnitFrameStack />
            <CastBar />
            <ActionBar getProjectionCenter={getProjectionCenter} />
          </HudScaleRegion>
        </>
      ) : null}
      {phase === "ended" ? <WinnerBanner /> : null}
    </div>
  );
}
